import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Send, Cpu, Circle, CheckSquare, Search, ArrowRight, User, AlertTriangle, UserCheck, ScrollText, Check } from 'lucide-react';
import { NexusUser } from '../types/nexus';

interface NexusChatProps {
  user: NexusUser;
  onAddTaskFromChat: (title: string) => void;
  onSearchFromChat: (query: string) => void;
  onDeployAgent: (role: string) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

interface Message {
  id: string;
  sender: 'user' | 'nexus';
  text: string;
  timestamp: string;
}

export const NexusChat: React.FC<NexusChatProps> = ({
  user,
  onAddTaskFromChat,
  onSearchFromChat,
  onDeployAgent,
  onAddLog
}) => {
  const [chatMode, setChatMode] = useState<'visitor' | 'client' | 'internal' | 'agent' | 'support' | 'sales'>('support');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'm-1',
      sender: 'nexus',
      text: 'Greetings. I am Nexus OS Intelligence, your cloud-integrated neural operating liaison. How can I facilitate your administrative workflows today?',
      timestamp: '03:17'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Chat modes configurations & plan restrictions
  const modeConfigs = {
    visitor: { label: 'Web Visitor Lobby', minPlan: 'Bronze', desc: 'Pre-sales automated lead responder.' },
    client: { label: 'Client Workspace Hub', minPlan: 'Silver', desc: 'Direct client administrative proxy.' },
    internal: { label: 'Internal Team Chat', minPlan: 'Silver', desc: 'Secure coordinate channel for teammates.' },
    support: { label: 'Support Assistant Node', minPlan: 'Bronze', desc: 'General queries solver and technical guide.' },
    sales: { label: 'Sales Assistant Core', minPlan: 'Silver', desc: 'Conversion analysis & B2B quote summaries.' },
    agent: { label: 'Agent Command Center', minPlan: 'Gold', desc: 'Command automated sub-agents directly.' }
  };

  // Check plan eligibility
  const checkPlanAccess = (mode: keyof typeof modeConfigs) => {
    const planHierarchy = { Bronze: 1, Silver: 2, Gold: 3, Platinum: 4 };
    const userRank = planHierarchy[user.plan];
    const reqPlanName = modeConfigs[mode].minPlan as 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
    const reqRank = planHierarchy[reqPlanName];

    return userRank >= reqRank;
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    if (!checkPlanAccess(chatMode)) {
      onAddLog('Nexus Chat', `Switched to restrict mode "${chatMode}"`, 'DENIED', `Access blocked. Plan "${user.plan}" cannot access Gold/Platinum modes.`);
      alert(`PLAN RESTRICTION!\nThe "${modeConfigs[chatMode].label}" requires a minimum of a ${modeConfigs[chatMode].minPlan} subscription.\n\nPlease upgrade your plan in the Billing Hub.`);
      return;
    }

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text: input,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    const storedInput = input;
    setInput('');
    setIsTyping(true);
    onAddLog('Nexus Chat', `User request: "${storedInput}"`, 'SUCCESS', 'Message dispatched into Chat processor.');

    // Simulated responsive AI feedback
    setTimeout(() => {
      let replyText = '';
      if (chatMode === 'agent') {
        replyText = `System directive parsed: "${storedInput}". Resolving task through autonomous worker threads. Triggering Search Agent nodes... Check active logs in the control plane.`;
      } else if (storedInput.toLowerCase().includes('task') || storedInput.toLowerCase().includes('todo')) {
        replyText = `Understood. I can construct a WorkSuite task item regarding that request. Please click "Create Task" below to commit it directly.`;
      } else if (storedInput.toLowerCase().includes('search') || storedInput.toLowerCase().includes('find')) {
        replyText = `Copy that. Scanning global indexes. If necessary, select "Execute Web Search" on this thread to feed query variables to Google Search API frameworks.`;
      } else {
        replyText = `Analytical processor completed: I have resolved search, hosting, and client parameters successfully. Nexus OS is ready to automate outbound content or compile reports on this topic. Direct voice assistance remains open on the 08000 622827 freephone frequency.`;
      }

      const nexusMsg: Message = {
        id: `n-${Date.now()}`,
        sender: 'nexus',
        text: replyText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, nexusMsg]);
      setIsTyping(false);
      onAddLog('Nexus Chat', 'AI response generated', 'SUCCESS', 'Successfully returned compiled AI answer.');
    }, 1200);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  return (
    <div className="w-full space-y-6">
      {/* Banner */}
      <div className="glass-panel border border-[#00ffff]/20 p-6 rounded-2xl bg-gradient-to-r from-purple-950/10 via-black/40 to-cyan-950/10">
        <h2 className="text-2xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
          <Cpu className="w-6 h-6 text-[#00ffff] animate-pulse" />
          Nexus Chat
        </h2>
        <p className="text-xs font-mono text-cyan-400/80 uppercase tracking-widest mt-1">
          Ask Nexus OS Intelligence & Escalate Commands
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Mode Sidebar Selector (1/4 col) */}
        <div className="lg:col-span-1 space-y-4">
          <div className="glass-panel border border-zinc-805 p-4 rounded-xl bg-black/40 space-y-3">
            <h3 className="text-[10px] font-mono font-black text-slate-500 uppercase tracking-widest border-b border-zinc-900 pb-2">
              Select Chat Mode
            </h3>

            <div className="flex flex-col gap-2.5 text-left text-xs font-mono">
              {Object.entries(modeConfigs).map(([key, cfg]) => {
                const isSelected = chatMode === key;
                const hasAccess = checkPlanAccess(key as any);

                return (
                  <button
                    key={key}
                    onClick={() => setChatMode(key as any)}
                    className={`p-2.5 rounded-lg border text-[10px] uppercase font-black tracking-wide text-left transition-all cursor-pointer relative ${
                      isSelected
                        ? 'bg-cyan-950/40 border-[#00ffff]/40 text-[#00ffff] shadow-[0_0_10px_rgba(0,255,255,0.08)]'
                        : 'bg-transparent border-zinc-900 text-slate-400 hover:border-zinc-800'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{cfg.label}</span>
                      {!hasAccess && (
                        <span className="text-[7.5px] font-mono text-amber-500 bg-amber-950/45 px-1.5 border border-amber-500/20 rounded">
                          LOCKED
                        </span>
                      )}
                    </div>
                    <span className="text-[8px] font-sans text-slate-500 block font-normal leading-normal pt-1 normal-case font-medium">
                      {cfg.desc}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Chat Terminal Area (3/4 col) */}
        <div className="lg:col-span-3 glass-panel border border-zinc-800 rounded-2xl bg-black/30 flex flex-col justify-between h-[520px] relative">
          {/* Header Status Bar */}
          <div className="bg-[#030612]/90 border-b border-zinc-850 p-4 rounded-t-2xl flex justify-between items-center font-mono">
            <div>
              <span className="text-[9px] text-slate-500 uppercase font-black">ACTIVE NODE ROUTE:</span>
              <h4 className="text-[10.5px] font-black text-white uppercase tracking-wider">
                {modeConfigs[chatMode].label}
              </h4>
            </div>
            <div className="flex items-center gap-2 text-[9.5px]">
              <Circle className="w-1.5 h-1.5 fill-emerald-500 text-emerald-500 animate-pulse" />
              <span className="text-emerald-400">ONLINE</span>
            </div>
          </div>

          {/* Messages Log Panel */}
          <div
            ref={scrollRef}
            className="flex-1 p-5 overflow-y-auto space-y-4 font-sans text-xs custom-scrollbar"
          >
            {messages.map((msg) => {
              const isNexus = msg.sender === 'nexus';

              return (
                <div
                  key={msg.id}
                  className={`flex gap-3 max-w-[85%] ${isNexus ? 'mr-auto text-left' : 'ml-auto flex-row-reverse text-right'}`}
                >
                  {/* Avatar Icons */}
                  <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 shadow-lg ${
                    isNexus
                      ? 'bg-cyan-950/40 border-cyan-500/30 text-[#00ffff]'
                      : 'bg-purple-950/40 border-purple-500/30 text-purple-300'
                  }`}>
                    {isNexus ? <Cpu size={14} className="stroke-[2.5]" /> : <User size={14} />}
                  </div>

                  <div className="space-y-1.5">
                    <div className={`p-4 rounded-2xl leading-relaxed font-sans font-medium text-slate-200 border ${
                      isNexus
                        ? 'bg-gradient-to-r from-cyan-950/10 to-black/40 border-zinc-850'
                        : 'bg-gradient-to-l from-purple-950/10 to-black/40 border-zinc-850'
                    }`}>
                      <p className="normal-case text-[11.5px] text-zinc-300">{msg.text}</p>

                      {/* Action buttons embedded in reply messages */}
                      {isNexus && (
                        <div className="mt-3.5 pt-2.5 border-t border-zinc-900/60 flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              onAddTaskFromChat(msg.text);
                              alert('Chat message converted to WorkSuite task!');
                            }}
                            className="py-1 px-2.5 bg-black/60 hover:bg-zinc-900 border border-zinc-800 hover:border-cyan-500/25 rounded text-[8.5px] font-mono uppercase tracking-wider text-slate-450 hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <CheckSquare size={10} />
                            Create Task
                          </button>
                          <button
                            onClick={() => {
                              onSearchFromChat(msg.text);
                            }}
                            className="py-1 px-2.5 bg-black/60 hover:bg-zinc-900 border border-zinc-800 hover:border-[#00ffff]/20 rounded text-[8.5px] font-mono uppercase tracking-wider text-[#00ffff]/80 hover:text-[#00ffff] flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <Search size={10} />
                            Search Web
                          </button>
                          <button
                            onClick={() => {
                              onDeployAgent('Customer Support Agent');
                              alert('Support agent triggered to analyze chat context!');
                            }}
                            className="py-1 px-2.5 bg-black/60 hover:bg-zinc-900 border border-zinc-800 hover:border-purple-500/20 rounded text-[8.5px] font-mono uppercase tracking-wider text-purple-300 hover:text-purple-200 flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <ArrowRight size={10} />
                            Escalate Agent
                          </button>
                        </div>
                      )}
                    </div>
                    <span className="text-[8.5px] font-mono text-slate-500 font-extrabold uppercase px-1">
                      {msg.timestamp}
                    </span>
                  </div>
                </div>
              );
            })}

            {isTyping && (
              <div className="flex gap-3 max-w-[85%] mr-auto text-left">
                <div className="w-8 h-8 rounded-full bg-cyan-950/40 border border-cyan-500/30 text-[#00ffff] flex items-center justify-center shrink-0">
                  <Cpu size={14} className="stroke-[2.5]" />
                </div>
                <div className="bg-gradient-to-r from-cyan-950/10 to-black/40 border border-zinc-850 p-3.5 rounded-2xl text-[11px] font-mono text-cyan-400 capitalize">
                  Nexus is formulating response...
                </div>
              </div>
            )}
          </div>

          {/* User Input controls */}
          <div className="bg-[#02040b]/90 border-t border-zinc-850 p-4 rounded-b-2xl">
            <form onSubmit={handleSendMessage} className="flex gap-2.5">
              <input
                type="text"
                placeholder={
                  checkPlanAccess(chatMode)
                    ? `Dispatch transaction instructions on [${modeConfigs[chatMode].label}]...`
                    : `RESTRICTED MODE: Upgrading to ${modeConfigs[chatMode].minPlan} premium plan is required.`
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={!checkPlanAccess(chatMode)}
                className="flex-grow bg-black/60 border border-zinc-805 rounded-xl px-4 py-3 text-xs font-mono text-white tracking-wide outline-none focus:border-[#00ffff]/40 uppercase disabled:opacity-40 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={!input.trim() || !checkPlanAccess(chatMode)}
                className="px-5 py-3 bg-gradient-to-r from-purple-600 to-[#00ffff] text-black rounded-xl cursor-pointer hover:opacity-95 active:scale-97 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-[0_0_12px_rgba(0,255,255,0.15)]"
              >
                <Send size={15} />
              </button>
            </form>

            <div className="flex flex-wrap gap-4 mt-3 text-[8px] font-mono text-slate-500 uppercase justify-between">
              <div>
                <span>* SSL Encrypted Terminal (Bypasses local telemetry scrapers)</span>
              </div>
              <button
                type="button"
                className="hover:text-white"
                onClick={() => {
                  onAddLog('Nexus Chat', 'Save transcript', 'SUCCESS', 'Secure chat ledger logs exported successfully.');
                  alert('Chat transcript session serialized & saved into Nexus Drive!');
                }}
              >
                📤 Export Log History
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
